import { Injectable } from '@angular/core';

/**
 * Provider descriptor returned by `GET /api/Settings/sso-providers`.
 *
 * `authorizationEndpoint` is the OIDC `authorization_endpoint` resolved
 * server-side from the issuer's `.well-known/openid-configuration` — the
 * SPA uses it directly so the PingFederate-specific default path isn't
 * hardcoded here.
 */
export interface SsoProviderDescriptor {
  type: string;
  issuer: string;
  clientId: string;
  scopes: string[];
  callbackPath: string;
  postLogoutRedirectUri?: string;
  authorizationEndpoint?: string;
  logoUrl?: string;
  /** When true on any of the tenant's providers, only configured providers get a login button. */
  exclusive?: boolean;
}

/**
 * Tokens-half of the callback result. The SPA hands these to the backend
 * `POST /api/User/loginSsoPing` endpoint, which does the actual token
 * exchange with the IdP using the client_secret stored in Key Vault.
 */
export interface PingCallbackPayload {
  code: string;
  codeVerifier: string;
  redirectUri: string;
  nonce: string;
}

const SESSION_KEY = 'ping_pkce';

interface PendingFlow {
  codeVerifier: string;
  state: string;
  nonce: string;
  redirectUri: string;
}

/**
 * Drives the Ping Identity OIDC Authorization Code + PKCE flow as a
 * confidential client: the SPA generates the verifier + challenge + nonce
 * and collects the code on the callback, but the actual token exchange is
 * delegated to the backend so the `client_secret` never reaches the
 * browser.
 *
 * Implemented against `window.crypto.subtle` directly so we avoid the
 * `oidc-client-ts` dependency (which would also drive the token
 * exchange itself).
 */
@Injectable({ providedIn: 'root' })
export class PingSsoService {

  private descriptor: SsoProviderDescriptor | null = null;

  configure(descriptor: SsoProviderDescriptor): void {
    this.descriptor = descriptor;
  }

  /**
   * Build the authorize URL and redirect the browser. The code_verifier,
   * expected state, and nonce are persisted to sessionStorage so the
   * callback handler can retrieve them after the round-trip to PingFederate.
   */
  async signIn(): Promise<void> {
    if (!this.descriptor) {
      throw new Error('PingSsoService.configure() must be called before signIn().');
    }
    if (!this.descriptor.authorizationEndpoint) {
      // The backend resolves this from the issuer's discovery document on every
      // /getSsoProviders call. If it's missing we can't safely redirect — fail
      // loud rather than fall back to a hardcoded vendor-specific path.
      throw new Error(
        'PingSsoService: authorizationEndpoint was not provided by the backend descriptor.',
      );
    }
    const scopes = this.descriptor.scopes && this.descriptor.scopes.length > 0
      ? this.descriptor.scopes
      : ['openid', 'email', 'profile'];

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await sha256ToBase64Url(codeVerifier);
    const state = generateRandomString(32);
    const nonce = generateRandomString(32);
    const redirectUri = `${window.location.origin}${this.descriptor.callbackPath}`;

    const pending: PendingFlow = { codeVerifier, state, nonce, redirectUri };
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(pending));

    const authorizeUrl = new URL(this.descriptor.authorizationEndpoint);
    authorizeUrl.searchParams.set('response_type', 'code');
    authorizeUrl.searchParams.set('client_id', this.descriptor.clientId);
    authorizeUrl.searchParams.set('redirect_uri', redirectUri);
    authorizeUrl.searchParams.set('scope', scopes.join(' '));
    authorizeUrl.searchParams.set('state', state);
    authorizeUrl.searchParams.set('nonce', nonce);
    authorizeUrl.searchParams.set('code_challenge', codeChallenge);
    authorizeUrl.searchParams.set('code_challenge_method', 'S256');

    window.location.assign(authorizeUrl.toString());
  }

  /**
   * Read the `code` and `state` returned to the callback URL, validate
   * against the stored state, and return the payload the backend needs to
   * complete the exchange (including the expected nonce so the backend
   * can assert the id_token's nonce claim matches this session).
   */
  completeSignIn(): PingCallbackPayload {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) {
      throw new Error('No pending Ping sign-in found in sessionStorage.');
    }
    const pending = JSON.parse(raw) as PendingFlow;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    if (error) {
      window.sessionStorage.removeItem(SESSION_KEY);
      throw new Error(`Ping returned authorization error: ${error}`);
    }
    if (!code || !state) {
      throw new Error('Ping callback is missing code or state.');
    }
    if (state !== pending.state) {
      window.sessionStorage.removeItem(SESSION_KEY);
      throw new Error('Ping callback state does not match — possible CSRF.');
    }

    // One-shot: clear immediately to prevent replay if the user reloads.
    window.sessionStorage.removeItem(SESSION_KEY);

    return {
      code,
      codeVerifier: pending.codeVerifier,
      redirectUri: pending.redirectUri,
      nonce: pending.nonce,
    };
  }
}

/**
 * Generate an RFC 7636-compliant `code_verifier`: 43+ unreserved chars.
 * 32 random bytes → 43 base64url chars, comfortably within the [43, 128] range.
 */
function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

function generateRandomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256ToBase64Url(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(input));
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes: Uint8Array): string {
  // btoa over a binary string; then make it URL-safe and strip padding.
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
